import { dateTime } from '@/_shared';

const MIN_SENT_OFFSET_SECONDS = 15 * 60;
const MAX_SENT_OFFSET_SECONDS = 20 * 60;
const FILE_TIME_ZONE = 'America/New_York';
const DOCUMENT_NAME = 'Terms and Conditions Agreement';

export type AuditTrailStamp = {
  date: string;
  time: string;
};

const emptyStamp = (): AuditTrailStamp => ({ date: 'N/A', time: '' });

/**
 * Sent time stays inside a 15–20 minute window before T&C page 4 was signed.
 * The offset is derived from that signed timestamp so a second download of the
 * same agreement keeps the same sent time.
 */
function sentOffsetSeconds(signedAtIso: string) {
  let hash = 0;
  for (let index = 0; index < signedAtIso.length; index += 1) {
    hash = (Math.imul(hash, 31) + signedAtIso.charCodeAt(index)) >>> 0;
  }

  const span = MAX_SENT_OFFSET_SECONDS - MIN_SENT_OFFSET_SECONDS;
  return MIN_SENT_OFFSET_SECONDS + (hash % (span + 1));
}

function toUtcStamp(value: { format: (template: string) => string }): AuditTrailStamp {
  return {
    date: value.format('YYYY/MM/DD'),
    time: `${value.format('HH:mm:ss')} UTC`,
  };
}

export function buildAuditTrailTimestamps(page4SignedAt: string | undefined) {
  const parsed = page4SignedAt ? dateTime.parse(page4SignedAt) : null;
  if (!parsed?.isValid()) {
    return {
      signedAt: emptyStamp(),
      sentAt: emptyStamp(),
      fileStamp: '',
    };
  }

  const signedAt = parsed.utc();
  const sentAt = signedAt.subtract(sentOffsetSeconds(page4SignedAt ?? ''), 'second');

  return {
    signedAt: toUtcStamp(signedAt),
    sentAt: toUtcStamp(sentAt),
    fileStamp: sentAt.tz(FILE_TIME_ZONE).format('M/D/YY, h:mm A'),
  };
}

export function buildAuditTrailFileName(firstName: string, lastName: string, fileStamp: string) {
  const signerName =
    [firstName, lastName]
      .map((part) => part.trim())
      .filter(Boolean)
      .join(' ') || 'Signer';
  const base = `${DOCUMENT_NAME} - ${signerName}`;
  return fileStamp ? `${base} - ${fileStamp}.pdf` : `${base}.pdf`;
}

export function toDownloadFileName(fileName: string) {
  return fileName.replace(/[\\/:*?"<>|]/g, '-');
}
